/**
 * FFmpeg.wasm converter module - Web Worker wrapper and helpers.
 * Used by VideoFormatter Vue page for browser-based video-to-MP4 conversion.
 */

const WORKER_SCRIPT = `
const T = {
  LOAD:"LOAD",EXEC:"EXEC",WRITE_FILE:"WRITE_FILE",
  READ_FILE:"READ_FILE",DELETE_FILE:"DELETE_FILE",RENAME:"RENAME",
  CREATE_DIR:"CREATE_DIR",LIST_DIR:"LIST_DIR",DELETE_DIR:"DELETE_DIR",
  ERROR:"ERROR",PROGRESS:"PROGRESS",LOG:"LOG"
};
let core;
const load = async ({ coreURL, wasmURL }) => {
  const first = !core;
  importScripts(coreURL);
  const wasmPath = wasmURL || coreURL.replace(/\\.js$/g, '.wasm');
  core = await self.createFFmpegCore({
    mainScriptUrlOrBlob: coreURL + '#' + btoa(JSON.stringify({ wasmURL: wasmPath }))
  });
  core.setLogger((e) => self.postMessage({ type: T.LOG, data: e }));
  core.setProgress((e) => self.postMessage({ type: T.PROGRESS, data: e }));
  return first;
};
const exec = ({ args, timeout }) => {
  if (typeof timeout === 'number' && timeout > 0) core.setTimeout(timeout);
  core.exec.apply(core, args);
  const ret = core.ret;
  core.reset();
  return ret;
};
const writeFile = ({ path, data }) => { core.FS.writeFile(path, data); return true; };
const readFile = ({ path, encoding }) => {
  if (encoding && encoding !== 'binary') return core.FS.readFile(path, { encoding: encoding });
  return core.FS.readFile(path);
};
const deleteFile = ({ path }) => { core.FS.unlink(path); return true; };
const rename = ({ oldPath, newPath }) => { core.FS.rename(oldPath, newPath); return true; };
const createDir = ({ path }) => { core.FS.mkdir(path); return true; };
const listDir = ({ path }) => {
  const names = core.FS.readdir(path);
  const nodes = [];
  for (const name of names) {
    const stat = core.FS.stat(path + "/" + name);
    const isDir = core.FS.isDir(stat.mode);
    nodes.push({ name: name, isDir: isDir });
  }
  return nodes;
};
const deleteDir = ({ path }) => { core.FS.rmdir(path); return true; };
self.onmessage = async function(e) {
  var d = e.data;
  var id = d.id, type = d.type, _data = d.data;
  const trans = [];
  var data;
  try {
    if (type !== T.LOAD && !core) throw new Error("not loaded");
    switch (type) {
      case T.LOAD: data = await load(_data); break;
      case T.EXEC: data = exec(_data); break;
      case T.WRITE_FILE: data = writeFile(_data); break;
      case T.READ_FILE: data = readFile(_data); break;
      case T.DELETE_FILE: data = deleteFile(_data); break;
      case T.RENAME: data = rename(_data); break;
      case T.CREATE_DIR: data = createDir(_data); break;
      case T.LIST_DIR: data = listDir(_data); break;
      case T.DELETE_DIR: data = deleteDir(_data); break;
      default: throw new Error("unknown message type");
    }
  } catch (err) {
    self.postMessage({ id: id, type: T.ERROR, data: err.toString() });
    return;
  }
  if (data && data instanceof Uint8Array) trans.push(data.buffer);
  self.postMessage({ id: id, type: type, data: data }, trans);
};
`;

const T = {
  LOAD: 'LOAD',
  EXEC: 'EXEC',
  WRITE_FILE: 'WRITE_FILE',
  READ_FILE: 'READ_FILE',
  DELETE_FILE: 'DELETE_FILE',
  RENAME: 'RENAME',
  CREATE_DIR: 'CREATE_DIR',
  LIST_DIR: 'LIST_DIR',
  DELETE_DIR: 'DELETE_DIR',
  ERROR: 'ERROR',
  PROGRESS: 'PROGRESS',
  LOG: 'LOG'
};

let workerBlobURL = null;

function getWorkerURL() {
  if (!workerBlobURL) {
    const blob = new Blob([WORKER_SCRIPT], { type: 'application/javascript' });
    workerBlobURL = URL.createObjectURL(blob);
  }
  return workerBlobURL;
}

class FFmpeg {
  constructor() {
    this._worker = null;
    this._resolves = {};
    this._rejects = {};
    this._logCbs = [];
    this._progressCbs = [];
    this._nextId = 0;
    this.loaded = false;
  }

  _onMsg = (e) => {
    const { id, type, data } = e.data;
    if (type === T.LOG) {
      this._logCbs.forEach(f => f(data));
      return;
    }
    if (type === T.PROGRESS) {
      this._progressCbs.forEach(f => f(data));
      return;
    }
    if (type === T.ERROR) {
      const rej = this._rejects[id];
      if (rej) rej(new Error(typeof data === 'string' ? data : JSON.stringify(data)));
    } else {
      if (type === T.LOAD) this.loaded = true;
      const res = this._resolves[id];
      if (res) res(data);
    }
    delete this._resolves[id];
    delete this._rejects[id];
  };

  _send(type, data, trans) {
    trans = trans || [];
    return new Promise((resolve, reject) => {
      const id = this._nextId++;
      this._worker.postMessage({ id, type, data }, trans);
      this._resolves[id] = resolve;
      this._rejects[id] = reject;
    });
  }

  async load(opts) {
    this._worker = new Worker(getWorkerURL());
    this._worker.onmessage = this._onMsg;
    this._worker.onerror = (e) => {
      console.error('Worker error:', e.message);
      Object.keys(this._rejects).forEach(id => {
        const rej = this._rejects[id];
        if (rej) rej(new Error('Worker error: ' + (e.message || 'unknown')));
        delete this._resolves[id];
        delete this._rejects[id];
      });
    };
    await this._send(T.LOAD, { coreURL: opts.coreURL, wasmURL: opts.wasmURL });
  }

  exec(args, timeout) {
    if (timeout === undefined) timeout = -1;
    return this._send(T.EXEC, { args, timeout });
  }

  writeFile(path, data) {
    const trans = [];
    if (data instanceof Uint8Array) trans.push(data.buffer);
    return this._send(T.WRITE_FILE, { path, data }, trans);
  }

  readFile(path, encoding) {
    if (encoding === undefined) encoding = 'binary';
    return this._send(T.READ_FILE, { path, encoding });
  }

  deleteFile(path) {
    return this._send(T.DELETE_FILE, { path });
  }

  rename(oldPath, newPath) {
    return this._send(T.RENAME, { oldPath, newPath });
  }

  createDir(path) {
    return this._send(T.CREATE_DIR, { path });
  }

  listDir(path) {
    return this._send(T.LIST_DIR, { path });
  }

  deleteDir(path) {
    return this._send(T.DELETE_DIR, { path });
  }

  on(event, cb) {
    if (event === 'log') this._logCbs.push(cb);
    else if (event === 'progress') this._progressCbs.push(cb);
  }

  off(event, cb) {
    if (event === 'log') this._logCbs = this._logCbs.filter(f => f !== cb);
    else if (event === 'progress') this._progressCbs = this._progressCbs.filter(f => f !== cb);
  }

  terminate() {
    if (this._worker) {
      this._worker.terminate();
      this._worker = null;
      this.loaded = false;
    }
  }
}

export const CORE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.js';
export const WASM_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.wasm';
export const MAX_FILE_SIZE = 100 * 1024 * 1024;
export const NUM_SEGMENTS = 15;
export const MAX_WORKERS = 3;
export const SPEED_PRESET = { preset: 'ultrafast', crf: '28' };

/** Video filter to keep same resolution as original (iw/ih = input width/height). */
export const VIDEO_FILTER_SAME_RESOLUTION = 'scale=iw:ih';

export function createFFmpegInstance() {
  const instance = new FFmpeg();
  return instance.load({ coreURL: CORE_URL, wasmURL: WASM_URL }).then(() => instance);
}

export async function getDuration(ffmpegInstance, inputName) {
  const logs = [];
  const logHandler = (e) => { logs.push(e.message); };
  ffmpegInstance.on('log', logHandler);
  await ffmpegInstance.exec(['-i', inputName, '-f', 'null', '-t', '0', '-']);
  ffmpegInstance.off('log', logHandler);
  for (let i = 0; i < logs.length; i++) {
    const line = logs[i];
    const match = line.match(/Duration:\s*(\d+):(\d+):(\d+)\.(\d+)/);
    if (match) {
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const seconds = parseInt(match[3], 10);
      const centis = parseInt(match[4], 10);
      return hours * 3600 + minutes * 60 + seconds + centis / 100;
    }
  }
  return 0;
}

export function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function formatDuration(ms, messages) {
  const t = messages || {};
  if (ms < 1000) return t.lessThan1s || 'less than 1s';
  const s = Math.round(ms / 1000);
  if (s < 60) return s + 's';
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m + 'm ' + rem + 's';
}

export function getProgressHint(progress, fileSizeMB, messages) {
  const m = messages || {};
  if (progress <= 10) return m.processingData || 'Processing video data...';
  if (progress <= 20) return m.optimizingQuality || 'Optimizing conversion quality...';
  if (progress <= 40) return m.encodingSegments || 'Encoding video segments...';
  if (progress <= 60) return m.compressingStream || 'Compressing video stream...';
  if (progress <= 80) return m.runningAlgorithms || 'Running conversion algorithms...';
  if (progress <= 100) return m.adjustingFrameRates || 'Adjusting frame rates...';

}

export function classifyError(err, logText, messages) {
  const m = messages || {};
  const msg = (err && err.message) || String(err || '');
  const lt = ((logText || '') + ' ' + msg).toLowerCase();
  if (lt.indexOf('invalid data') !== -1 || lt.indexOf('corrupt') !== -1 || lt.indexOf('moov atom not found') !== -1) {
    return m.corrupted || 'The video file appears to be corrupted and cannot be converted.';
  }
  if (lt.indexOf('codec not currently supported') !== -1 || lt.indexOf('decoder') !== -1 || lt.indexOf('unknown codec') !== -1 || lt.indexOf('no decoder') !== -1) {
    return m.unsupportedCodec || 'This video uses an unsupported codec that cannot be decoded in the browser.';
  }
  if (lt.indexOf('invalid argument') !== -1 || lt.indexOf('no such file') !== -1) {
    return m.invalidFile || 'The file could not be read. It may not be a valid video file.';
  }
  if (lt.indexOf('worker error') !== -1 || lt.indexOf('importscripts') !== -1 || lt.indexOf('network') !== -1) {
    return m.engineFailed || 'Failed to load the conversion engine. Check your internet connection and try again.';
  }
  return msg || m.defaultError || 'Conversion failed. The file may be corrupted or use an unsupported codec.';
}

export function fetchFileData(file) {
  return file.arrayBuffer().then(ab => new Uint8Array(ab));
}

export function createJobId() {
  return 'job_' + Math.random().toString(36).substring(2, 10) + Date.now();
}

export function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
